import { useMutateConnectIntegrationEhrLaunch } from '@/hooks/mutation/integration/ehr-launch/useMutateConnectIntegrationEhrLaunch';
import { useMutateUpdateIntegrationEhrLaunch } from '@/hooks/mutation/integration/ehr-launch/useMutateUpdateIntegrationEhrLaunch';
import { useQueryUserEHRLaunchProfiles } from '@/hooks/query/integrations/useQueryUserEHRLaunchProfiles';
import { logger } from '@/utils/logger/logger';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useQueryKindeProperties } from '../query/useQueryKindeProperties';

export function useAutoCreateEhrProfile() {
  const router = useRouter();

  const {
    data: userKindeProperties,
    isError: isKindePropertiesError,
    error: kindePropertiesError,
  } = useQueryKindeProperties();

  const { data: userEhrLaunchProfiles } = useQueryUserEHRLaunchProfiles();

  const {
    mutate: connectEhrLaunchIntegration,
    isPending: isConnectingEhrLaunchIntegration,
    isError: isConnectingEhrLaunchIntegrationError,
    error: connectingEhrLaunchIntegrationError,
  } = useMutateConnectIntegrationEhrLaunch();
  const {
    mutate: updateEhrLaunchIntegration,
    isPending: isUpdatingEhrLaunchIntegration,
    isError: isUpdatingEhrLaunchIntegrationError,
    error: updatingEhrLaunchIntegrationError,
  } = useMutateUpdateIntegrationEhrLaunch();

  // Note: We should prevent retrying when error
  useEffect(() => {
    const apiError =
      connectingEhrLaunchIntegrationError || updatingEhrLaunchIntegrationError;

    // Temporary debug log to check mutation errors
    if (apiError) {
      logger.error(
        '[INT] AutoCreateEhrProfile: previous mutation error detected. Halting further attempts.',
        apiError
      );
    }

    // Log Kinde properties query error
    if (kindePropertiesError) {
      logger.error(
        '[INT] AutoCreateEhrProfile: Kinde properties query failed. Halting further attempts.',
        kindePropertiesError
      );
    }

    if (
      !userEhrLaunchProfiles ||
      !userKindeProperties?.epicUserId ||
      !userKindeProperties?.connectionId ||
      isConnectingEhrLaunchIntegration ||
      isUpdatingEhrLaunchIntegration ||
      isConnectingEhrLaunchIntegrationError ||
      isUpdatingEhrLaunchIntegrationError ||
      isKindePropertiesError
    ) {
      return;
    }

    if (userEhrLaunchProfiles.length === 0) {
      logger.info(
        '[INT] AutoCreateEhrProfile: attempting connectEhrLaunchIntegration (auto-created)'
      );
      connectEhrLaunchIntegration(
        {
          ehr_provider: EHRProvider.Epic_Systems,
          internal_id: userKindeProperties.epicUserId,
          connection_id: userKindeProperties.connectionId,
          is_auto_created: true,
        },
        {
          onSuccess: () => {
            void router.push('/integrations');
          },
        }
      );
    } else if (
      userEhrLaunchProfiles.find((x) => x.internal_id !== userKindeProperties.epicUserId)
    ) {
      logger.info(
        '[INT] AutoCreateEhrProfile: attempting updateEhrLaunchIntegration to sync internal_id'
      );
      updateEhrLaunchIntegration({
        request: {
          internal_id: userKindeProperties.epicUserId,
        },
        provider: EHRProvider.Epic_Systems,
      });
    }
  }, [
    connectEhrLaunchIntegration,
    router,
    updateEhrLaunchIntegration,
    userEhrLaunchProfiles,
    userKindeProperties,
    isUpdatingEhrLaunchIntegration,
    isUpdatingEhrLaunchIntegrationError,
    isConnectingEhrLaunchIntegration,
    isConnectingEhrLaunchIntegrationError,
    connectingEhrLaunchIntegrationError,
    updatingEhrLaunchIntegrationError,
    isKindePropertiesError,
    kindePropertiesError,
  ]);
}
